import { FiLoader } from 'react-icons/fi'
import styled, { css, keyframes } from 'styled-components'
import { pxToRem } from 'styles/functions'

const spin = keyframes`
  from {
    transform:rotate(0deg);
  }
  to {
    transform:rotate(360deg);
  }
`

interface styleButtonFlatProps {
  theme: any;
  $themeCustom: any;
}
const styleButtonFlat = css<styleButtonFlatProps>`
  width: 66%;
  margin: auto;
  padding: 2px 6px;
  font-size: 12px;
  line-height: 15px;

  border-radius: ${({ theme, $themeCustom }) => pxToRem($themeCustom ? $themeCustom.details.buttons.radius : theme.button_radius/1.5 || 0)};
`;

interface StyledLoaderProps {
  theme: any;
}
export const StyledLoader = styled(FiLoader)<StyledLoaderProps>`
  ${({ theme }) => theme.trigger_hide_loader && 'display: none'};
  margin-right: ${pxToRem(10)};
  animation: ${spin} 3s linear infinite;
`

const getButtonColor = (theme, isPhotobooth, $themeCustom) => {
  const color = $themeCustom ? $themeCustom.details.buttons.color : theme.button_color || '#34b076'
  if (isPhotobooth) {
    return theme.photobooth_button_color || color
  }
  return color
}

const getTextColor = (theme, isPhotobooth) => {
  if (isPhotobooth) {
    return theme.photobooth_button_text_color || theme.button_text_color || 'white'
  }
  return theme.button_text_color || 'white'
}

interface StyledButtonContainerProps {
  theme: any;
}
export const StyledButtonContainer = styled.div<StyledButtonContainerProps>`
  display: flex;
  align-items: ${({ theme }) => (theme.button_centered ? 'center' : 'flex-start')};
  margin: ${pxToRem(20)};
  flex-flow: column;
`

export const StyledTextBefore = styled.div`
    width: 100%;
    font-size: ${({ theme }) => (theme.text_size ? pxToRem(theme.text_size) : '14px')};
    text-align: ${({ theme }) => (theme.button_text_before_after_align ? theme.button_text_before_after_align : 'center')};
    margin-bottom: 3px;
`

export const StyledTextAfter = styled.div`
    width: 100%;
    font-size: ${({ theme }) => (theme.text_size ? pxToRem(theme.text_size) : '14px')};
    text-align: ${({ theme }) => (theme.button_text_before_after_align ? theme.button_text_before_after_align : 'center')};
    margin-top: 3px;
`

interface StyledButtonProps {
  theme: any;
  $isPhotobooth: boolean;
  $flat: boolean;
  $themeCustom: any;
}
export const StyledButton = styled.a<StyledButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-wrap: wrap;

  ${({ theme }) => theme.button_background_url && css`
    background: url(${theme.button_background_url});
  `};

  background-color: ${({ theme, $isPhotobooth, $themeCustom }) => getButtonColor(theme, $isPhotobooth, $themeCustom)};
  color: ${({ theme, $isPhotobooth }) => getTextColor(theme, $isPhotobooth)};
  padding: ${({ theme }) => (theme.button_padding ? theme.button_padding : pxToRem(12))};
  width: ${({ theme }) => (theme.button_width ? pxToRem(theme.button_width) : '100%')};

  ${({ theme, $isPhotobooth, $themeCustom }) =>
          !theme.button_no_border &&
          css`
            border: ${pxToRem(theme.button_border_width || 2)} solid ${theme.button_border_color || getButtonColor(theme, $isPhotobooth, $themeCustom)};
          `}

  border-radius: ${({ theme, $themeCustom }) => pxToRem($themeCustom ? $themeCustom.details.buttons.radius : theme.button_radius || 0)};
  box-shadow: ${({ theme }) => theme.button_shadow || 'none'};
  transition: all 0.2s;
  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 0.1);
  cursor: pointer;

  &.disabled {
    color: white;
    pointer-events: none;
    background-color: lightgrey;
    border-color: lightgrey;
    cursor: not-allowed;
  }

  label {
    display: flex;
    align-items: center;
    cursor: pointer;
    pointer-events: none;
    ${({ theme }) =>
            theme.button_text_size &&
            css`
              font-size: ${pxToRem(theme.button_text_size)};
            `};
    ${({ theme }) =>
            theme.button_font &&
            css`
              font-family: ${theme.button_font};
            `};
    ${({ theme }) =>
            theme.button_text_uppercase &&
            css`
              text-transform: uppercase;
            `};
    ${({ theme }) =>
            theme.button_text_weight &&
            css`
              font-weight: ${theme.button_text_weight};
            `};
    ${({ theme }) =>
            theme.button_text_lineheight &&
            css`
              line-height: ${pxToRem(theme.button_text_lineheight)};
            `};
    ${({ theme }) =>
            theme.button_text_letter_spacing &&
            css`
              letter-spacing: ${pxToRem(theme.button_text_letter_spacing)};
            `};
  }

  &:hover:not(.disabled, :focus) {
    background-color: ${({ theme }) => (theme.button_hover_color ? theme.button_hover_color : 'transparent')};

    color: ${({ theme, $isPhotobooth, $themeCustom }) =>
            theme.button_hover_text_color || theme.button_text_color || getButtonColor(theme, $isPhotobooth, $themeCustom)};

    ${({ theme }) =>
            (theme.button_hover_color || theme.button_hover_border_color) &&
            css`
              border-color: ${({ theme }) => (theme.button_hover_border_color ? theme.button_hover_border_color : theme.button_border_color)};
            `}
  }

  ${({ theme }) => theme.custom_button && theme.custom_button}
    
  ${({ $flat, $themeCustom }) => $flat && css`${styleButtonFlat}`};
`

interface StyledButtonOutlinedProps {
  theme: any;
  $isPhotobooth: boolean;
  $flat: boolean;
}
export const StyledButtonOutlined = styled(StyledButton)<StyledButtonOutlinedProps>`
  background-color: ${({ theme }) => theme.button_outlined_color || 'transparent'};
  color: ${({ theme, $isPhotobooth, $themeCustom }) =>
    theme.button_outlined_text_color || getButtonColor(theme, $isPhotobooth, $themeCustom)};
  ${({ theme }) => theme.button_outlined_text_color && `border-color: ${theme.button_outlined_text_color}`};
  border: ${({ theme, $themeCustom }) =>
    `2px solid ${($themeCustom && $themeCustom.details.buttons.color) || theme.button_outlined_border_color || theme.framed_background || theme.button_color || '#34b076'}`};
  text-wrap: wrap;

  &:hover:not(.disabled, :focus) {
    background-color: ${({ theme, $isPhotobooth, $themeCustom }) =>
      theme.button_outlined_hover_color || getButtonColor(theme, $isPhotobooth, $themeCustom)};
    color: ${({ theme, $themeCustom }) =>
      $themeCustom ? 'white' : theme.button_outlined_hover_text_color || 'white'};
    border-color: ${({ theme, $isPhotobooth, $themeCustom }) =>
      theme.button_outlined_hover_border_color
        ? theme.button_outlined_hover_border_color
        : theme.button_outlined_hover_color || getButtonColor(theme, $isPhotobooth, $themeCustom)};
  }

  ${({ theme }) => theme.custom_alternate_button && theme.custom_alternate_button}

  ${({ $flat, $themeCustom }) =>
    $flat &&
    css`
      ${styleButtonFlat}
    `};
`
